import React, { forwardRef } from "react";

import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import {
  fieldAccentColor,
  fieldBorderColor,
  fieldErrorColor,
  fieldErrorRing,
  fieldFocusRing,
  fieldHoverRing,
} from "../../../theme/fieldChrome";

type BaseTextFieldProps = Omit<MuiTextFieldProps, "variant" | "onChange">;

export interface TextFieldProps extends BaseTextFieldProps {
  /**
   * Convenience handler that emits the string value whenever it changes.
   * Useful for components that expect a plain string instead of the native event.
   */
  onValueChange?: (value: string) => void;
  /**
   * Shortcut props for common adornment usage without having to pass InputProps.
   */
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const StyledTextField = styled(MuiTextField)(({ theme }) => {
  // Light mode: white fields pop on the grey card; dark mode keeps the grey fill.
  const defaultBackground =
    theme.palette.mode === "light"
      ? "#ffffff"
      : theme.palette.primary?.[50] || "#f6f6f6";
  const hoverBackground =
    theme.palette.gray?.[50] || theme.palette.primary?.[100] || "#ededed";
  const defaultBorder = fieldBorderColor(theme);

  return {
    "& .MuiFilledInput-root": {
      borderRadius: 8,
      backgroundColor: defaultBackground,
      border: `1px solid ${defaultBorder}`,
      padding: 0,
      transition:
        "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
      // HOVER — brand border + a soft glow (`fieldHoverRing`), the same treatment
      // every other field in the design system now carries (§31 — one source,
      // `fieldChrome`, rather than this component's own hand-picked
      // `theme.palette.brand?.[600] || "#7F48DF"` literal). Guarded against
      // `disabled` below, since `.Mui-disabled` is declared AFTER this block at
      // equal specificity (class + pseudo-class vs class + class) — without an
      // explicit override there, a disabled-but-hovered field would still pick up
      // this rule's `boxShadow` (cascade is per-PROPERTY, not per rule-block; a
      // later rule that doesn't mention a property leaves an earlier one's value
      // in place for it).
      "&:hover": {
        backgroundColor: hoverBackground,
        borderColor: fieldAccentColor(theme),
        boxShadow: fieldHoverRing(theme),
      },
      "&.Mui-focused": {
        backgroundColor: theme.palette.white?.[100] || "#ffffff",
        borderColor: fieldAccentColor(theme),
        boxShadow: fieldFocusRing(theme),
      },
      "&.Mui-disabled": {
        backgroundColor: defaultBackground,
        borderColor: defaultBorder,
        // Explicit `none`, not just "absent" — see the comment on `&:hover` above:
        // without this, hovering a disabled field would still show the hover ring,
        // since `.Mui-disabled` (later, equal specificity) only overrides the
        // properties it actually declares.
        boxShadow: "none",
        opacity: 0.5,
        cursor: "not-allowed",
      },
      // READ-ONLY — MUI adds `.MuiInputBase-readOnly` to the root whenever a
      // top-level `readOnly` prop reaches the underlying `InputBase` (e.g. a caller
      // passing `<TextField readOnly />` or `InputProps={{ readOnly: true }}`). A
      // field you cannot type into must not advertise that you can, same as
      // `disabled` above — just without the dimmed opacity, since read-only content
      // should stay fully legible.
      "&.MuiInputBase-readOnly": {
        "&:hover": {
          borderColor: defaultBorder,
          boxShadow: "none",
        },
      },
      /*
        INVALID — kept identical to the sibling `Input` in this same package, which is
        the component most fields in both apps actually render. The two disagreed until
        now, and the disagreement was in the state that matters most: `boxShadow: "none"`
        here KILLED the focus ring on an errored field, so an invalid field going into
        focus lost its halo entirely while a valid one gained a brand halo. That reads as
        the error state being LESS interactive than the healthy one.

        The ring is not removed, it is RECOLOURED — same 3px geometry, same 6% alpha,
        error colour instead of brand. Nesting it under `&.Mui-error` (0,3,0 against the
        plain `&.Mui-focused` at 0,2,0) means the outcome does not depend on which order
        emotion emits the two.
      */
      "&.Mui-error": {
        borderColor: fieldErrorColor(theme),
        "&:hover": {
          borderColor: fieldErrorColor(theme),
          // No brand ring on an errored field on hover either — see the `Input`
          // sibling's identical note: the state a field is IN outranks the state
          // it is being touched in.
          boxShadow: "none",
        },
        "&.Mui-focused": {
          backgroundColor: theme.palette.white?.[100] || "#ffffff",
          borderColor: fieldErrorColor(theme),
          boxShadow: fieldErrorRing(theme),
        },
      },
      "& .MuiFilledInput-input": {
        padding: "12px 14px",
        fontSize: 16,
      },
    },
    "& .MuiInputAdornment-root": {
      color: theme.palette.text?.secondary || theme.palette.primary?.[400],
    },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text?.secondary || theme.palette.primary?.[400],
      opacity: 1,
    },
  };
});

const assignRef = (
  ref: React.Ref<HTMLInputElement> | undefined,
  value: HTMLInputElement | null
) => {
  if (!ref) return;
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  try {
    (ref as React.MutableRefObject<HTMLInputElement | null>).current = value;
  } catch {
    // no-op if the ref is read-only
  }
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      InputProps: InputPropsProp,
      startAdornment,
      endAdornment,
      onValueChange,
      onChange,
      fullWidth = true,
      ...rest
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange?.(event.target.value);
      onChange?.(event);
    };

    const baseInputProps = InputPropsProp || {};
    const mergedInputProps = {
      disableUnderline: true,
      ...baseInputProps,
      startAdornment:
        baseInputProps.startAdornment !== undefined
          ? baseInputProps.startAdornment
          : startAdornment,
      endAdornment:
        baseInputProps.endAdornment !== undefined
          ? baseInputProps.endAdornment
          : endAdornment,
      inputRef: (instance: HTMLInputElement | null) => {
        assignRef(ref, instance);
        assignRef(baseInputProps.inputRef, instance);
      },
    };

    return (
      <StyledTextField
        {...rest}
        variant="filled"
        fullWidth={fullWidth}
        InputProps={mergedInputProps}
        onChange={handleChange}
      />
    );
  }
);
